<template>
  <!--
    Modal genérico del Asistente de IA, reusable en cualquier sección del PEI -- consolida
    lo que antes eran dos componentes casi idénticos (ver AiSuggestedObjetivesModal.vue,
    ya eliminado). Solo aporta el "shell": header (ícono + título + acciones), estados de
    loading/error, y tres modos de presentación:
      - 'small' (por defecto al abrir): diálogo flotante compacto, no angosta ni oscurece.
      - 'docked': panel fijo a la derecha -- Layout.vue angosta el contenido vía Vuex.
      - 'fullscreen': modal centrado con backdrop.
    El botón de pin alterna 'small'/'docked'; el de expandir alterna con 'fullscreen'.
    El CONTENIDO del cuerpo lo decide 100% quien usa este modal, vía el slot por defecto.
  -->
  <transition :name="mode === 'docked' ? 'ai-panel-slide' : 'ai-modal-fade'">
    <div v-if="visible" class="ai-modal-root">
      <div
        class="ai-modal-backdrop"
        :class="{
          'ai-modal-backdrop--docked': mode === 'docked',
          'ai-modal-backdrop--small': mode === 'small',
        }"
        @click.self="handleBackdropClick"
      >
        <div
          class="ai-modal-small-shell"
          :class="{ 'ai-modal-small-shell--active': mode === 'small' }"
          :style="mode === 'small' ? smallShellStyle : null"
        >
        <div
          ref="modalBox"
          class="ai-modal"
          :class="{
            'ai-modal--docked': mode === 'docked',
            'ai-modal--small': mode === 'small',
          }"
          :style="mode === 'small' ? smallStyle : null"
          role="dialog"
          :aria-modal="mode === 'fullscreen' ? 'true' : 'false'"
        >
          <div class="ai-modal-header">
            <div class="ai-modal-header-title">
              <span class="ai-modal-bot-icon">
                <AiSparkIcon :size="15" variant="white" />
              </span>
              <strong>{{ title }}</strong>
            </div>
            <div class="ai-modal-header-actions">
              <!-- Pin: alterna 'small'/'docked'. Oculto en fullscreen. SVG propio (ícono no disponible en el kit de FA). -->
              <button
                v-if="mode !== 'fullscreen'"
                type="button"
                class="ai-modal-icon-btn"
                :class="{ 'ai-modal-icon-btn--active': mode === 'docked' }"
                :aria-label="mode === 'docked' ? 'Minimizar' : 'Anclar al panel derecho'"
                :title="mode === 'docked' ? 'Minimizar' : 'Anclar al panel derecho'"
                @click="togglePin"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="12" cy="5" r="2.5" stroke="currentColor" stroke-width="1.8" />
                  <line x1="12" y1="7.5" x2="12" y2="19" stroke="currentColor" stroke-width="1.8" />
                  <line x1="7" y1="10" x2="17" y2="10" stroke="currentColor" stroke-width="1.8" />
                  <path
                    d="M4 13a8 8 0 0 0 16 0"
                    stroke="currentColor"
                    stroke-width="1.8"
                    fill="none"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                class="ai-modal-icon-btn"
                :aria-label="mode === 'fullscreen' ? 'Restaurar' : 'Pantalla completa'"
                :title="mode === 'fullscreen' ? 'Restaurar' : 'Pantalla completa'"
                @click="toggleFullscreen"
              >
                <!-- fa-expand / fa-compress (rejillas de esquinas) se leian como una cruz a
                     14px, indistinguibles de un icono de "mover"/"fijar". Se usan las
                     variantes "-alt" (flechas diagonales), que ademas son las que muestra el
                     mockup de Figma para este boton. -->
                <i :class="mode === 'fullscreen' ? 'fas fa-compress-alt' : 'fas fa-expand-alt'" />
              </button>
              <button
                type="button"
                class="ai-modal-close"
                aria-label="Cerrar"
                @click="close"
              >
                <i class="fas fa-times" />
              </button>
            </div>
          </div>

          <div class="ai-modal-body">
            <!-- "Contexto: <campo>" (Figma) -- caja con degradado lavanda que indica sobre
                 que campo esta trabajando el asistente; se muestra tambien durante el loading,
                 como en el mockup. NO es el bloque "Contexto detectado" que el PO pidio ocultar
                 (aquel listaba las fuentes que encontro el Orquestador, ver Tab3.vue). -->
            <div v-if="contextLabel" class="ai-modal-context-chip">
              <span class="ai-modal-context-chip-title">Contexto:</span>
              <span class="ai-modal-context-chip-value">{{ contextLabel }}</span>
            </div>

            <div v-if="error" class="ai-modal-error">
              {{ error }}
            </div>

            <!-- Loading tomado del mockup de Figma: anillo con degradado violeta (#ba65ff,
                 el mismo tono del esquema del asistente), title en negrita y una segunda
                 linea gris que dice que campo esta redactando el asistente. -->
            <div v-if="loading" class="ai-modal-loading">
              <div class="ai-modal-spinner" aria-hidden="true" />
              <p class="ai-modal-loading-title">{{ loadingText }}</p>
              <p v-if="loadingSubtext" class="ai-modal-loading-subtitle">
                {{ loadingSubtext }}
              </p>
            </div>

            <!-- El cuerpo (campos, tarjetas de sugerencias, acciones, refinar, lo que sea)
                 vive 100% del lado de quien usa este modal -- este componente no sabe ni le
                 importa qué hay adentro. -->
            <template v-if="!loading && !error">
              <slot />

              <!--
                Documentos que la IA leyó para esta sugerencia. El PEI es un documento legal y
                quien firma es el especialista, así que tiene que poder ver de dónde salió cada
                dato: qué documento se usó, cómo se leyó (texto del PDF u OCR) y si se pudo
                confirmar que pertenece a ESTE estudiante. El enlace abre el documento original.
              -->
              <div v-if="hasDocuments" class="ai-modal-docs">
                <p class="ai-modal-docs-title">
                  <i class="fas fa-file-alt" aria-hidden="true" />
                  Documentos consultados
                </p>
                <ul class="ai-modal-docs-list">
                  <li v-for="doc in documents" :key="doc.documentId" class="ai-modal-docs-item">
                    <button
                      type="button"
                      class="ai-modal-docs-link"
                      :disabled="downloadingDocumentId === doc.documentId"
                      @click="openDocument(doc)"
                    >
                      {{ doc.label || 'Documento adjunto' }}
                    </button>
                    <span class="ai-modal-docs-meta">{{ documentMeta(doc) }}</span>
                    <span :class="['ai-modal-docs-badge', identityBadgeClass(doc)]">
                      {{ identityBadgeText(doc) }}
                    </span>
                    <span v-if="doc.note" class="ai-modal-docs-note">{{ doc.note }}</span>
                  </li>
                </ul>
              </div>

              <!--
                "Refinar Resultado" -- vive acá (no en cada padre) para que el input, el botón
                y sobre todo el estilo (incluyendo el :focus violeta) sean UNA sola copia. El
                padre solo decide SI mostrarlo (`canRefine`, según si ya hay algo que refinar) y
                qué placeholder mostrar; el ajuste pedido llega por el evento `refine` con el
                texto ya validado/recortado -- el padre sigue siendo quien llama a la API
                (refineSuggestion) y decide qué hacer con la respuesta, porque eso sí difiere
                por sección (un solo texto vs una lista de objetivos).
              -->
              <div v-if="canRefine" class="ai-modal-refine">
                <p class="ai-modal-refine-label">Refinar Resultado:</p>
                <div class="ai-modal-refine-row">
                  <input
                    v-model="refineInstruction"
                    type="text"
                    class="ai-modal-refine-input"
                    :placeholder="refinePlaceholder"
                    :disabled="loading"
                    @keyup.enter="submitRefine"
                  />
                  <button
                    type="button"
                    class="ai-modal-refine-btn"
                    :disabled="loading || !refineInstruction.trim()"
                    @click="submitRefine"
                  >
                    <span class="ai-modal-refine-btn-text">Refinar</span>
                  </button>
                </div>
              </div>

              <!--
                "¿Te fue útil esta sugerencia?" -- mismo motivo que arriba: el padre solo dice
                SI mostrarlo (`canGiveFeedback`) y el estado vigente (`feedback`,
                `feedbackSubmitting`); el texto queda fijo acá a propósito -- que cada sección
                tuviera su propia copia era justo lo que permitía que el texto y el estilo
                se fueran separando sin que nadie lo notara.
              -->
              <div v-if="canGiveFeedback" class="ai-modal-feedback">
                <span class="ai-modal-feedback-label">¿Te fue útil esta sugerencia?</span>
                <div class="ai-modal-feedback-actions">
                  <button
                    type="button"
                    class="ai-modal-feedback-btn"
                    :class="{ 'ai-modal-feedback-btn--up': feedback === 'up' }"
                    :disabled="feedbackSubmitting"
                    aria-label="Sí, me fue útil"
                    title="Sí, me fue útil"
                    @click="$emit('feedback', true)"
                  >
                    <i class="fas fa-thumbs-up" />
                  </button>
                  <button
                    type="button"
                    class="ai-modal-feedback-btn"
                    :class="{ 'ai-modal-feedback-btn--down': feedback === 'down' }"
                    :disabled="feedbackSubmitting"
                    aria-label="No, no me fue útil"
                    title="No, no me fue útil"
                    @click="$emit('feedback', false)"
                  >
                    <i class="fas fa-thumbs-down" />
                  </button>
                </div>
              </div>

              <!--
                Bloque de "Buenas prácticas" -- contenido CONFIGURABLE, no hardcodeado: llega
                por la prop `bestPractices` con { title, items[], linkText, linkUrl }.
                La idea es que salga de la misma configuración por Módulo/Sección-Área que ya
                se edita en Operaciones > Configuración de Prompts de IA (views/operations/
                aiPromptConfig), de modo que cada sección del PEI muestre sus propias
                recomendaciones. Si no viene configurado, no se pinta nada.
              -->
              <div v-if="hasBestPractices" class="ai-modal-tips">
                <p class="ai-modal-tips-title">
                  <i class="fas fa-check-circle" />
                  {{ bestPractices.title }}
                </p>
                <ul class="ai-modal-tips-list">
                  <li v-for="(punto, index) in bestPractices.items" :key="index">
                    <i class="fas fa-check" />
                    <span>{{ punto }}</span>
                  </li>
                </ul>
                <a
                  v-if="bestPractices.linkText && bestPractices.linkUrl"
                  class="ai-modal-tips-link"
                  :href="bestPractices.linkUrl"
                  target="_blank"
                  rel="noopener"
                >
                  {{ bestPractices.linkText }}
                </a>
              </div>
            </template>
          </div>
        </div>
        <div
          v-if="mode === 'small' && anchorRect"
          class="ai-modal-tail"
          :class="'ai-modal-tail--' + tailPlacement"
          :style="smallTailStyle"
        />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import AiSparkIcon from '@/components/pei/shared/ai-assistant/AiSparkIcon.vue';
import documentsService from '@/services/documents';

// Bloqueo de scroll de body compartido por TODAS las instancias de este componente (ver
// syncSmallScrollLock más abajo) -- contador a nivel de módulo, no por instancia, porque
// document.body es uno solo pero puede haber varios diálogos de IA montados a la vez (uno
// por Meta/Objetivo). Antes cada instancia guardaba y restauraba su propio "valor anterior"
// de overflow; en la cadena "Meta con Asistente IA" -> "Objetivo con Asistente IA" el segundo
// diálogo podía abrir mientras el primero todavía no terminaba de destrabar el body, capturaba
// 'hidden' como su "valor previo", y al cerrar lo dejaba en 'hidden' para siempre (bug
// reportado: scroll de la página trabado incluso después de cerrar todos los diálogos). Con el
// contador, el body solo se destraba cuando el ÚLTIMO diálogo abierto cierra, sin importar el
// orden de apertura/cierre entre instancias.
let smallScrollLockCount = 0;
let smallScrollLockPrevOverflow = null;

export default {
  name: 'AiAssistantModal',
  components: {
    AiSparkIcon,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    // Texto del header -- distingue de qué asistente se trata (ej. "Asistente Virtual"
    // para III-A/III-B, "Objetivos Sugeridos por IA" para AG-05).
    title: {
      type: String,
      default: 'Asistente Virtual',
    },
    loadingText: {
      type: String,
      default: 'Generando Redacción Sugerida...',
    },
    // Segunda linea del estado de carga (Figma: "El asistente está redactando <campo>").
    loadingSubtext: {
      type: String,
      default: '',
    },
    // Campo/seccion sobre la que trabaja el asistente -- se pinta en la caja "Contexto:"
    // arriba del cuerpo. Vacio = no se pinta la caja.
    contextLabel: {
      type: String,
      default: '',
    },
    // Recomendaciones de redaccion configurables por seccion:
    // { title: String, items: String[], linkText?: String, linkUrl?: String }.
    // null / sin items = el bloque no se muestra.
    bestPractices: {
      type: Object,
      default: null,
    },
    // Documentos leídos por la IA para esta sugerencia (ver PeiAISourceDocumentInfo en el
    // backend): [{ documentId, label, extractionMethod, pageCount, identityVerification,
    // used, note }]. Vacío = no se pinta el bloque (el caso de las secciones que no tienen
    // fuentes documentales).
    documents: {
      type: Array,
      default: () => [],
    },
    // getBoundingClientRect() del botón que disparó el asistente. Usado en modo 'small'
    // para abrir el diálogo pegado a ese botón, con una colita apuntando hacia él.
    anchorRect: {
      type: Object,
      default: null,
    },
    // "Refinar Resultado" -- true cuando el padre ya tiene una sugerencia generada sobre la
    // que tenga sentido pedir un ajuste (ej. fields.length / suggestions.length). false = el
    // bloque no se pinta (nada que refinar todavía).
    canRefine: {
      type: Boolean,
      default: false,
    },
    // Placeholder del input de "Refinar" -- lo único que sí varía por sección (el tipo de
    // ajuste que tiene sentido pedir no es el mismo para un texto libre que para una lista
    // de objetivos), a diferencia del estilo y el texto del label, que quedan fijos acá.
    refinePlaceholder: {
      type: String,
      default: 'Ej: resumir, tono más técnico, enfatiza conducta...',
    },
    // "¿Te fue útil esta sugerencia?" -- mismo criterio que canRefine: el padre decide SI se
    // muestra (ya hay sugerencia Y ya se tiene el aiGenerationLogId de la corrida vigente).
    canGiveFeedback: {
      type: Boolean,
      default: false,
    },
    // 'up' | 'down' | null -- estado vigente del feedback de la corrida actual.
    feedback: {
      type: String,
      default: null,
    },
    feedbackSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasBestPractices() {
      return !!(
        this.bestPractices &&
        this.bestPractices.title &&
        Array.isArray(this.bestPractices.items) &&
        this.bestPractices.items.length
      );
    },
    hasDocuments() {
      return Array.isArray(this.documents) && this.documents.length > 0;
    },
  },
  data() {
    return {
      // 'small' | 'docked' | 'fullscreen' -- 15 sep: arranca con el último modo elegido en
      // CUALQUIER diálogo del Asistente de IA (Vuex `aiAssistantPreferredMode`), no siempre
      // en 'small'. Esto es lo que hace que la cadena "Meta con Asistente IA" -> "Objetivo
      // con Asistente IA" (AG-05) respete, p.ej., que el especialista había anclado
      // ("pineado") el diálogo de Metas al panel derecho -- el de Objetivos que se abre
      // solo a continuación es una instancia de Vue distinta y sin esto volvía a arrancar
      // flotante. El watch de `visible` de abajo vuelve a leer este valor en cada apertura,
      // por si la preferencia cambió mientras este diálogo estaba oculto.
      mode: this.$store.state.aiAssistantPreferredMode || 'small',
      // Modo al que volver al salir de 'fullscreen'.
      modeBeforeFullscreen: 'small',
      // Id del documento que se está bajando -- deshabilita ese enlace mientras tanto.
      downloadingDocumentId: null,
      // Posición (top/bottom/left) del "shell" que envuelve modal + colita -- ver
      // .ai-modal-small-shell. Vive aparte de smallStyle porque es EL shell (no .ai-modal)
      // quien ahora es position: fixed; así ambos hijos comparten un solo
      // filter: drop-shadow, en vez de tener cada uno su propia sombra independiente.
      smallShellStyle: {},
      // Ya sin top/bottom/left (esos se movieron a smallShellStyle) -- solo maxHeight,
      // que sigue siendo de .ai-modal (el que hace scroll). Calculado en
      // computeSmallPosition() a partir de `anchorRect`.
      smallStyle: {},
      // 'top' | 'bottom' -- de qué borde del diálogo sale la colita (ver template).
      tailPlacement: 'top',
      smallTailStyle: {},
      // Input local de "Refinar Resultado" -- vive acá (no en el padre) porque el componente
      // ahora es dueño de todo el bloque; el padre solo recibe el texto ya recortado por el
      // evento `refine` cuando el especialista lo envía.
      refineInstruction: '',
    };
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        // 15 sep -- en vez de forzar siempre 'small', respeta el último modo elegido
        // (ver comentario en `mode` dentro de data()) por si cambió mientras este
        // diálogo particular estaba oculto.
        this.mode = this.$store.state.aiAssistantPreferredMode || 'small';
      }
      this.syncDockedState();
      this.syncSmallScrollLock();
    },
    mode(newValue) {
      this.syncDockedState();
      this.syncSmallScrollLock();
      // 15 sep -- recuerda el modo actual en Vuex para que el próximo diálogo de IA
      // (otra instancia de este mismo componente) arranque igual.
      this.$store.commit('setAiAssistantPreferredMode', newValue);
      if (newValue === 'small') {
        this.$nextTick(this.computeSmallPosition);
      }
    },
    anchorRect() {
      if (this.mode === 'small') {
        this.$nextTick(this.computeSmallPosition);
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.computeSmallPosition);
  },
  // Las pestañas del PEI viven dentro de <keep-alive> (ver Master.vue), así que cambiar de
  // pestaña NO destruye este componente: lo desactiva. Sin estos dos hooks, el panel
  // anclado desaparecía de la pantalla (su DOM sale del documento) pero el flag
  // `aiAssistantDocked` seguía en true, y Layout.vue dejaba el contenido angostado con una
  // franja blanca a la derecha hasta que se cambiara de ruta. beforeDestroy no alcanza
  // para esto porque nunca llega a ejecutarse al cambiar de pestaña.
  deactivated() {
    this.releaseSharedLayoutState();
    window.removeEventListener('resize', this.computeSmallPosition);
  },
  // Al volver a la pestaña se restaura el estado que el diálogo tenía: si seguía abierto y
  // anclado, el layout se vuelve a angostar solo; si estaba en 'small', se recupera su
  // bloqueo de scroll.
  activated() {
    window.removeEventListener('resize', this.computeSmallPosition);
    window.addEventListener('resize', this.computeSmallPosition);
    this.syncDockedState();
    this.syncSmallScrollLock();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    submitRefine() {
      const instruccion = this.refineInstruction.trim();
      if (!instruccion) return;
      this.$emit('refine', instruccion);
      this.refineInstruction = '';
    },
    // Bloquea el scroll de la PÁGINA (no del diálogo, que sigue haciendo su propio
    // scroll interno via overflow-y: auto) mientras el diálogo 'small' está abierto.
    // A diferencia de 'docked' (que a propósito deja seguir interactuando con el
    // contenido de fondo -- ver .ai-modal-backdrop--docked) o 'fullscreen' (que sí
    // tapa todo con un backdrop oscuro), 'small' es un diálogo flotante ANCLADO a un
    // botón específico de la página: su posición (`anchorRect`) se captura una sola vez
    // al abrirlo y no se vuelve a medir con el scroll. Si el usuario hace scroll, el
    // botón se mueve pero el diálogo/colita (position: fixed dentro del shell) se quedan
    // clavados donde estaban -- da la impresión de que "se abrió en otro lado". Bloquear
    // el scroll mientras está en 'small' evita esa desconexión visual.
    syncSmallScrollLock() {
      const shouldLock = this.visible && this.mode === 'small';
      if (shouldLock && !this.$_smallScrollLockActive) {
        if (smallScrollLockCount === 0) {
          smallScrollLockPrevOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
        }
        smallScrollLockCount += 1;
        this.$_smallScrollLockActive = true;
      } else if (!shouldLock && this.$_smallScrollLockActive) {
        smallScrollLockCount = Math.max(0, smallScrollLockCount - 1);
        if (smallScrollLockCount === 0) {
          document.body.style.overflow = smallScrollLockPrevOverflow || '';
          smallScrollLockPrevOverflow = null;
        }
        this.$_smallScrollLockActive = false;
      }
    },
    // Alterna entre 'small' y 'docked'.
    togglePin() {
      this.mode = this.mode === 'docked' ? 'small' : 'docked';
    },
    toggleFullscreen() {
      if (this.mode === 'fullscreen') {
        this.mode = this.modeBeforeFullscreen;
      } else {
        this.modeBeforeFullscreen = this.mode;
        this.mode = 'fullscreen';
      }
    },
    handleBackdropClick() {
      // En modo docked/small el backdrop tiene pointer-events: none (no dimea la
      // pantalla), así que este click ni le llega -- solo aplica al backdrop del modo
      // fullscreen.
      this.close();
    },
    // Refleja en Vuex si el layout compartido (Layout.vue) debe angostar el contenido
    // principal para reservarle espacio al panel docked.
    //
    // Solo escribe `false` la instancia que puso `true` ($_ownsDockedState). En una misma
    // pestaña conviven varias instancias de este modal (Tab3, GoalsManager,
    // SubAreaNecessity, SubAreaGoalCallout) y casi todas están con visible = false: sin
    // esta marca de propiedad, cualquiera de ellas podría pisar con `false` el `true` que
    // acaba de escribir la que sí está abierta (p. ej. al reactivarse la pestaña, donde
    // el orden de los hooks entre hermanos no está garantizado).
    syncDockedState() {
      const shouldDock = this.visible && this.mode === 'docked';

      if (shouldDock) {
        this.$_ownsDockedState = true;
        this.$store.commit('setIsAiAssistantDocked', true);
      } else if (this.$_ownsDockedState) {
        this.$_ownsDockedState = false;
        this.$store.commit('setIsAiAssistantDocked', false);
      }
    },
    // Suelta TODO el estado compartido que este diálogo pueda estar reteniendo: el
    // margen del layout (Vuex) y el bloqueo de scroll del body. Se usa tanto al
    // destruirse el componente como al desactivarse dentro de <keep-alive>.
    releaseSharedLayoutState() {
      if (this.$_ownsDockedState) {
        this.$_ownsDockedState = false;
        this.$store.commit('setIsAiAssistantDocked', false);
      }

      if (this.$_smallScrollLockActive) {
        smallScrollLockCount = Math.max(0, smallScrollLockCount - 1);
        if (smallScrollLockCount === 0) {
          document.body.style.overflow = smallScrollLockPrevOverflow || '';
          smallScrollLockPrevOverflow = null;
        }
        this.$_smallScrollLockActive = false;
      }
    },
    // Posiciona el diálogo 'small' pegado al botón que lo disparó (anchorRect), con la
    // colita apuntando hacia él -- arriba del botón si hay espacio, si no, abajo.
    // Texto corto con cómo se leyó el documento -- le dice al especialista si lo que la IA
    // "leyó" salió del texto real del PDF o de un OCR sobre un escaneo, que es justo donde
    // pueden aparecer errores de reconocimiento en cifras y nombres.
    documentMeta(doc) {
      const partes = [];

      if (doc.extractionMethod === 'PdfTextLayer') partes.push('texto del PDF');
      if (doc.extractionMethod === 'FormRecognizerOcr') partes.push('OCR de documento escaneado');
      if (doc.pageCount) partes.push(`${doc.pageCount} pág.`);
      if (doc.used === false) partes.push('no utilizado');

      return partes.join(' · ');
    },
    identityBadgeText(doc) {
      if (doc.identityVerification === 'Confirmada') return 'Estudiante verificado';
      if (doc.identityVerification === 'Contradictoria') return 'Otro estudiante';

      return 'Sin verificar';
    },
    identityBadgeClass(doc) {
      if (doc.identityVerification === 'Confirmada') return 'ai-modal-docs-badge--ok';
      if (doc.identityVerification === 'Contradictoria') return 'ai-modal-docs-badge--error';

      return 'ai-modal-docs-badge--warn';
    },
    async openDocument(doc) {
      if (!doc || !doc.documentId || this.downloadingDocumentId) return;

      this.downloadingDocumentId = doc.documentId;

      try {
        await documentsService.downloadDocumentByAttachmentIdOrDocumentId(doc.documentId);
      } finally {
        this.downloadingDocumentId = null;
      }
    },
    computeSmallPosition() {
      if (!this.anchorRect) {
        this.smallShellStyle = {};
        this.smallStyle = {};
        this.smallTailStyle = { display: 'none' };
        return;
      }
      const margin = 14;
      const edgeGap = 12;
      // Ancho tomado del mockup de Figma (nodo "Frame 8174", ~623px -- ahí el diálogo es
      // "ancho y bajo", no angosto y alto: con más ancho el texto entra en menos líneas).
      const modalWidth = 620;
      // Tamaño y proporción de "tapado" de la colita, tomados del nodo "Polygon 1" del
      // mismo mockup (76x66, ~56% de su alto oculto detrás de la tarjeta y ~44% asomando).
      const tailWidth = 76;
      const tailHeight = 66;
      const tailHiddenRatio = 0.56;
      const anchor = this.anchorRect;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const estimatedHeight = 360;

      const openAbove = anchor.top > estimatedHeight + margin;
      this.tailPlacement = openAbove ? 'bottom' : 'top';

      // Alineado al borde izquierdo del botón (con un pequeño corrimiento), no centrado:
      // centrar un diálogo de 420px sobre un botón que vive cerca del margen izquierdo del
      // formulario lo empuja siempre contra el borde de la pantalla (pegado al menú lateral).
      let left = anchor.left - 24;
      left = Math.max(edgeGap, Math.min(left, viewportWidth - modalWidth - edgeGap));

      // top/bottom/left ahora posicionan el SHELL (.ai-modal-small-shell), no .ai-modal
      // directamente -- así modal + colita comparten un solo filter: drop-shadow aplicado
      // al shell, en vez de que cada uno tenga su propia sombra independiente (que es lo
      // que impedía que se vieran "conectados" como una sola pieza, igual que en Figma).
      const shellStyle = { left: `${left}px` };
      if (openAbove) {
        shellStyle.bottom = `${viewportHeight - anchor.top + margin}px`;
      } else {
        shellStyle.top = `${anchor.bottom + margin}px`;
      }
      this.smallShellStyle = shellStyle;

      // maxHeight sigue siendo de .ai-modal (el que hace scroll con overflow-y: auto) --
      // el shell en sí no tiene alto fijo, se ajusta al contenido.
      this.smallStyle = {
        maxHeight: openAbove
          ? `${anchor.top - margin - edgeGap}px`
          : `${viewportHeight - anchor.bottom - margin - edgeGap}px`,
      };

      // Colita: ahora es position: absolute DENTRO del shell (ya no position: fixed en
      // coordenadas de viewport), así que sus offsets son relativos a la esquina del
      // shell. Para el eje Y se usa top/bottom (según hacia dónde abrió el diálogo) en vez
      // de una coordenada absoluta -- así no hace falta conocer el alto real del modal
      // (que varía según el contenido: loading, error, texto generado, etc.), el offset
      // es válido sin importar cuánto mida el shell.
      const anchorCenterX = anchor.left + anchor.width / 2;
      const tailLeft = Math.max(
        16,
        Math.min(anchorCenterX - tailWidth / 2 - left, modalWidth - tailWidth - 16)
      );
      // Porción de la colita que asoma fuera del shell (el resto, tailHiddenRatio, queda
      // tapado detrás de .ai-modal gracias a su z-index más alto).
      const tailOverhang = tailHeight * (1 - tailHiddenRatio);
      this.smallTailStyle = openAbove
        ? { left: `${tailLeft}px`, bottom: `${-tailOverhang}px` }
        : { left: `${tailLeft}px`, top: `${-tailOverhang}px` };
    },
  },
  beforeDestroy() {
    // Red de seguridad: si el componente se destruye (ej. navegación a otra ruta) sin
    // pasar por close(), no dejar el layout compartido angostado ni la página con
    // overflow: hidden para siempre. Misma liberación que al desactivarse por keep-alive.
    this.releaseSharedLayoutState();
    window.removeEventListener('resize', this.computeSmallPosition);
  },
};
</script>

<style scoped>
.ai-modal-root {
  /* Agrupa el backdrop bajo un único nodo raíz (Vue 2 requiere un solo elemento raíz en el
     template) -- no pinta nada por sí mismo. */
  position: relative;
}

/*
  Transición de entrada/salida del modo fullscreen. Vue aplica estas clases automáticamente
  por el nombre del <transition> del template.
*/
.ai-modal-fade-enter-active,
.ai-modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.ai-modal-fade-enter-active .ai-modal,
.ai-modal-fade-leave-active .ai-modal {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.ai-modal-fade-enter,
.ai-modal-fade-leave-to {
  opacity: 0;
}

.ai-modal-fade-enter .ai-modal,
.ai-modal-fade-leave-to .ai-modal {
  opacity: 0;
  transform: translateY(-12px);
}

.ai-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(17, 41, 27, 0.45);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 60px;
}

.ai-modal {
  /* Ancho tomado del mockup de Figma (el frame "Asistente Virtual" ocupa ~85% del ancho de
     la página en Figma -- ahí es "ancho y bajo" en vez de "angosto y alto"). Lo llevamos aún
     más cerca del ancho completo de la pantalla (95%) porque, al centrarse sobre el VIEWPORT
     completo (que incluye el menú lateral que el contenido real de la página no ocupa), un
     modal angosto queda con su borde izquierdo metido dentro de esa franja del menú -- eso lo
     desalineaba del área de contenido real. Acercar el modal al ancho total de la pantalla
     reduce ese desfase y lo deja visualmente centrado sobre el contenido, como en Figma. */
  width: 95%;
  max-width: 1400px;
  max-height: 85vh;
  overflow-y: auto;
  background: #fff;
  /* Borde lila y esquinas de 12px tomados del mockup de Figma (la tarjeta del asistente
     flota sobre el contenido con un borde #e9e1ff, no a canto vivo). */
  border: 1px solid #e9e1ff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.ai-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-radius: 12px 12px 0 0;
  /* En Figma el header del asistente es BLANCO con el title en el gris oscuro de marca
     (#11291b): el violeta vive en el circulo del bot y en los botones, no en una barra
     solida. Ver AiSuggestionIcon.vue y .ai-objetive-trigger en SubAreaGoalCallout.vue para
     el resto de piezas del asistente con el mismo esquema. */
  background: #fff;
}

.ai-modal-header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #11291b;
  font-family: 'Lato', sans-serif;
  font-size: 18px;
}

.ai-modal-bot-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  /* Degradado + anillo lila #e9e1ff exactamente como el nodo "Bot Icon" de Figma. */
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  border: 3px solid #e9e1ff;
  color: #fff;
  font-size: 14px;
}

.ai-modal-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-modal-icon-btn {
  border: none;
  background: transparent;
  /* Gris de marca (#bac7c0) sobre el header blanco, como en Figma. */
  color: #bac7c0;
  font-size: 14px;
  cursor: pointer;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ai-modal-icon-btn:hover {
  background: #f4f1ff;
  color: #5c58ed;
}

/* ---- Documentos consultados por la IA ---- */
.ai-modal-docs {
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #e6e0f5;
  border-radius: 10px;
  background: #faf8ff;
}

.ai-modal-docs-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #4b3f66;
}

.ai-modal-docs-title i {
  margin-right: 6px;
}

.ai-modal-docs-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ai-modal-docs-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
  color: #6b6480;
}

.ai-modal-docs-link {
  padding: 0;
  border: 0;
  background: none;
  font-size: 12px;
  font-weight: 600;
  color: #7b3fe4;
  text-decoration: underline;
  cursor: pointer;
}

.ai-modal-docs-link:disabled {
  opacity: 0.6;
  cursor: progress;
}

.ai-modal-docs-badge {
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.ai-modal-docs-badge--ok {
  background: #e3f6ea;
  color: #1b7a43;
}

.ai-modal-docs-badge--warn {
  background: #fdf3e0;
  color: #8a6116;
}

.ai-modal-docs-badge--error {
  background: #fdeaea;
  color: #a32020;
}

.ai-modal-docs-note {
  flex-basis: 100%;
  font-size: 11px;
  font-style: italic;
}

/* Estado activo del botón de pin cuando el modal está 'docked'. */
.ai-modal-icon-btn--active {
  background: #f4f1ff;
  color: #5c58ed;
}

.ai-modal-close {
  border: none;
  background: transparent;
  color: #bac7c0;
  font-size: 16px;
  cursor: pointer;
}

.ai-modal-close:hover {
  color: #5c58ed;
}

/* Caja "Contexto:" -- degradado lavanda horizontal (#e7deff -> #c9b3fc) y textos violeta,
   muestreados del mockup de Figma. */
.ai-modal-context-chip {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, #e7deff 0%, #c9b3fc 100%);
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

.ai-modal-context-chip-title {
  font-weight: 700;
  color: #5c58ed;
}

.ai-modal-context-chip-value {
  font-weight: 700;
  color: #8b5cf6;
}

/* En Figma hay ~40px entre la caja de contexto y el contenido; el gap del cuerpo aporta 18.
   (Regla sobre la caja misma: el contenido que sigue viene por slot y no lleva el scope-id
   de este componente, asi que un selector de hermano no lo alcanzaria.) */
.ai-modal-context-chip {
  margin-bottom: 16px;
}

.ai-modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow-y: auto;
}

.ai-modal-error {
  background: #ffe4df;
  color: #c9545e;
  border-radius: 4px;
  padding: 12px 14px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

.ai-modal-refine {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-modal-refine-label {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: #11291b;
  margin: 0;
}

.ai-modal-refine-row {
  display: flex;
  gap: 10px;
}

.ai-modal-refine-input {
  flex: 1;
  min-width: 0;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  padding: 10px 12px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: #11291b;
}

.ai-modal-refine-input::placeholder {
  color: #9e9e9e;
}

/* Antes solo existía en Tab3.vue -- SubAreaGoalCallout.vue tenía una copia del resto de este
   bloque pero nunca de esta regla, por eso su input se veía con el foco negro por defecto del
   navegador en vez del violeta del asistente. Ahora hay una sola copia, para los dos. */
.ai-modal-refine-input:focus {
  outline: none;
  border-color: #a062fb;
  box-shadow: 0 0 0 3px rgba(92, 88, 237, 0.15);
}

.ai-modal-refine-btn {
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  padding: 10px 18px;
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, #5c58ed 0%, #ba65ff 100%) border-box;
}

.ai-modal-refine-btn:hover:not(:disabled) {
  background:
    linear-gradient(#f4f1ff, #f4f1ff) padding-box,
    linear-gradient(135deg, #5c58ed 0%, #ba65ff 100%) border-box;
}

.ai-modal-refine-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ai-modal-refine-btn-text {
  background: linear-gradient(135deg, #5c58ed 0%, #ba65ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: #7c5cf3;
  -webkit-text-fill-color: transparent;
}

.ai-modal-feedback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #d9e5df;
  font-family: 'Lato', sans-serif;
}

.ai-modal-feedback-label {
  font-size: 13px;
  color: #88948d;
}

.ai-modal-feedback-actions {
  display: flex;
  gap: 6px;
}

.ai-modal-feedback-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  border: 1px solid #e2e2e2;
  background: #fff;
  color: #88948d;
  font-size: 13px;
  cursor: pointer;
}

.ai-modal-feedback-btn:hover:not(:disabled) {
  border-color: #a062fb;
  color: #5c58ed;
  background: #f6f3ff;
}

.ai-modal-feedback-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ai-modal-feedback-btn--up {
  border-color: #2db782;
  color: #2db782;
  background: #e4fff5;
}

.ai-modal-feedback-btn--down {
  border-color: #c9545e;
  color: #c9545e;
  background: #ffe4df;
}

/* Callout de recomendaciones. Va en la MISMA linea violeta del asistente (#5c58ed -> #ba65ff,
   borde #e9e1ff) y no en verde: el verde de marca es el del resto de la app, y dentro de este
   panel todo lo que pertenece al asistente se lee como violeta. */
.ai-modal-tips {
  background: #f6f3ff;
  border: 1px solid #e9e1ff;
  border-radius: 8px;
  padding: 14px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

.ai-modal-tips-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  font-weight: 700;
  color: #5c58ed;
}

.ai-modal-tips-list {
  list-style: none;
  margin: 0;
  padding: 0;
  color: #11291b;
}

.ai-modal-tips-list li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 3px 0;
  line-height: 1.45;
}

.ai-modal-tips-list li i {
  color: #a062fb;
  font-size: 12px;
  margin-top: 4px;
}

.ai-modal-tips-link {
  display: inline-block;
  margin-top: 10px;
  color: #7c5cf3;
  font-weight: 600;
  text-decoration: none;
}

.ai-modal-tips-link:hover {
  text-decoration: underline;
}

.ai-modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  color: #11291b;
  font-family: 'Lato', sans-serif;
  /* En Figma el loading queda centrado en el alto del panel, no pegado arriba. */
  flex: 1;
  min-height: 240px;
  padding: 24px 0;
}

/* Anillo con degradado violeta (#ba65ff, el tono final del gradiente del asistente) que se
   desvanece hasta transparente -- el mask recorta el centro para dejarlo hueco. */
.ai-modal-spinner {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  margin-bottom: 14px;
  background: conic-gradient(
    from 90deg,
    rgba(186, 101, 255, 0) 0deg,
    rgba(186, 101, 255, 0.15) 90deg,
    rgba(186, 101, 255, 0.55) 240deg,
    #ba65ff 340deg,
    #ba65ff 360deg
  );
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
  animation: ai-modal-spin 1s linear infinite;
}

@keyframes ai-modal-spin {
  to {
    transform: rotate(360deg);
  }
}

.ai-modal-loading-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #11291b;
}

.ai-modal-loading-subtitle {
  margin: 0;
  font-size: 14px;
  color: #88948d;
}

/* ---- Modo docked: panel fijo al lado derecho, sin oscurecer el contenido principal ---- */

.ai-modal-backdrop--docked {
  /* A diferencia del backdrop del modo fullscreen, este no dimea la pantalla -- Layout.vue
     ya le reserva espacio angostando .page-content (ver pageContentStyle() ahí). pointer-events:
     none dejar pasar los clics hacia la página debajo del panel en vez de bloquear todo el
     viewport como haría un backdrop normal. */
  background: transparent;
  padding-top: 0;
  align-items: stretch;
  justify-content: flex-end;
  pointer-events: none;
}

/* 'small': no dimea la pantalla ni angosta el layout, pero no ocupa todo el alto. */
.ai-modal-backdrop--small {
  background: transparent;
  padding-top: 96px;
  padding-right: 24px;
  align-items: flex-start;
  justify-content: flex-end;
  pointer-events: none;
}

.ai-modal--docked {
  /* top: 0 a propósito (no 85px como el offset de #sidebar) -- el panel debe llegar hasta
     el borde superior real de la ventana, en la misma fila que el navbar, ocupando la
     columna derecha que Navigation.vue ya le vacía (ver navStyle() ahí, angosta el navbar
     con el mismo right: 440px). Corregido a pedido del usuario tras verlo renderizado: el
     panel se quedaba corto por 85px respecto al borde superior real. */
  pointer-events: auto;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  /* 556px es el ancho real del modal en Figma (nodo 6084:24960, "556 x 1070") -- antes eran
     440px y por eso todo se veia mas apretado que el diseno. Sale de la variable global
     --ai-assistant-panel-width (assets/scss/v2/main.scss), que tambien leen Layout.vue y
     Navigation.vue para angostar el contenido y el navbar exactamente lo mismo; el clamp de
     esa variable lo hace responsive (39vw, sin bajar de 380px). */
  width: var(--ai-assistant-panel-width, 556px);
  max-width: 100%;
  max-height: none;
  border-radius: 0;
  /* Solo el borde izquierdo: el panel sigue pegado al borde derecho de la ventana (no es la
     tarjeta flotante de Figma), pero conserva el filo lila #e9e1ff del mockup. */
  border: none;
  border-left: 1px solid #e9e1ff;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
}

.ai-modal--docked .ai-modal-header {
  border-radius: 0;
  flex-shrink: 0;
}

/* El panel docked ocupa todo el alto de la ventana: se hace columna flex para que el cuerpo
   estire y el estado de carga pueda quedar centrado verticalmente, como en Figma. */
.ai-modal--docked {
  display: flex;
  flex-direction: column;
}

.ai-modal--docked .ai-modal-body {
  flex: 1;
}

/* Transición de entrada/salida del modo docked -- desliza desde la derecha en vez del
   fade+slide vertical del modo fullscreen. */
.ai-panel-slide-enter-active .ai-modal--docked,
.ai-panel-slide-leave-active .ai-modal--docked {
  transition: transform 0.22s ease;
}

.ai-panel-slide-enter .ai-modal--docked,
.ai-panel-slide-leave-to .ai-modal--docked {
  transform: translateX(100%);
}

/* Fallback móvil: por debajo del breakpoint que ya usa el resto de la app (ver App.vue,
   MediaQueryProvider mobile: '(max-width: 680px)'), el panel docked ocupa toda la pantalla
   en vez de angostar el contenido -- a 440px fijos sería inutilizable en un teléfono. */
@media (max-width: 680px) {
  .ai-modal--docked {
    width: 100%;
  }
}

/* Envuelve a .ai-modal y a .ai-modal-tail en modo 'small' para que compartan un solo
   filter: drop-shadow -- así se ven como una sola pieza (tarjeta + colita), igual que en
   el mockup de Figma (ahí son un solo path vectorial con un solo efecto de sombra). En
   docked/fullscreen es `display: contents` -- no genera caja propia, así que no afecta en
   nada el layout/posicionamiento que ya tenían esos modos (.ai-modal sigue siendo su
   propio position: fixed en 'docked', o un hijo normal del backdrop flex en 'fullscreen').
   Solo en 'small' pasa a ser un bloque real, position: fixed (con top/bottom/left por
   :style inline desde computeSmallPosition()) -- y AHÍ es seguro ponerle un filter,
   porque ni .ai-modal ni .ai-modal-tail usan ya position: fixed puertas adentro (usan
   relative/absolute) -- un filter en un ancestro con hijos position: fixed rompería sus
   coordenadas (los convertiría en el "containing block" de esos hijos), pero con hijos
   relative/absolute no hay ese problema. */
.ai-modal-small-shell {
  display: contents;
}

.ai-modal-small-shell--active {
  display: block;
  position: fixed;
  z-index: 2;
  pointer-events: none;
  filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.25));
}

/* 'small': diálogo flotante compacto, pegado al botón que lo abrió. El propio
   position: fixed (con top/bottom/left por :style inline) ahora vive en el "shell" que
   envuelve a .ai-modal y a .ai-modal-tail (ver .ai-modal-small-shell) -- no aquí. Esto
   permite que el shell les ponga a los DOS un solo filter: drop-shadow compartido, como
   una sola pieza, igual que en Figma (antes cada uno tenía su propia sombra por separado
   y se veían "cortadas" una de la otra). Por eso también se anula el box-shadow propio
   de `.ai-modal` acá -- si no, se verían dos sombras superpuestas. */
.ai-modal--small {
  pointer-events: auto;
  position: relative;
  width: 620px;
  max-width: calc(100vw - 48px);
  overflow-y: auto;
  box-shadow: none;
  /* CAMBIO REAL (no solo de color): `.ai-modal` (regla base) le pone a la tarjeta un
     `border: 1px solid #e9e1ff` en las 4 caras. Ese borde es lo que de verdad hacía que
     la colita se viera "pegada", más que el color -- la línea del borde cruza derecho
     por donde la colita nace, como si la tarjeta le pusiera una tapa encima, sin importar
     qué tan bien funda su relleno (ver el degradado en .ai-modal-tail--bottom/--top). En
     Figma no pasa esto porque tarjeta + colita son UNA sola forma (un solo boolean union),
     así que ahí no hay ningún borde recto cruzando la base de la colita. Sin poder replicar
     esa unión real sin rehacer todo con un clip-path dinámico (la tarjeta cambia de alto
     según el contenido: loading, error, texto generado...), se anula el borde acá -- la
     tarjeta small queda definida solo por la sombra compartida del shell, sin línea que
     "tape" la colita. Encima de la colita (z-index más bajo) para que la parte que se
     solapa quede tapada detrás de la tarjeta, como en el mockup de Figma. */
  border: none;
  z-index: 2;
  border-radius: 10px;
}

@media (max-width: 680px) {
  .ai-modal--small {
    width: 100%;
    max-width: calc(100vw - 24px);
  }
}

/* Colita del diálogo 'small' -- forma de banderín (clip-path, tomada del nodo
   "Polygon 1" del mockup de Figma), no un simple cuadrado rotado. z-index menor que
   .ai-modal--small para que la parte que se solapa con la tarjeta quede tapada detrás
   de ella, igual que en el mockup. position: absolute (ya no fixed) porque ahora vive
   DENTRO de .ai-modal-small-shell (que sí es fixed) -- sigue siendo hermana de .ai-modal,
   no hija suya, así el overflow-y: auto del cuerpo no la recorta. Sus offsets (top/bottom
   + left) son relativos al shell, calculados en computeSmallPosition(). Ya no tiene su
   propio filter: drop-shadow -- ahora ese filter vive en el shell, compartido con
   .ai-modal, para que ambos lean como una sola pieza con una sola sombra (igual que en
   Figma, donde tarjeta + colita son un solo path con un solo efecto de sombra). */
.ai-modal-tail {
  position: absolute;
  /* 76x66 -- debe calzar con tailWidth/tailHeight en computeSmallPosition() (JS), que
     calcula top/left asumiendo esta medida. Si difieren, la porción "asomando" de la
     colita se reduce a unos pocos px y queda prácticamente invisible. */
  width: 76px;
  height: 66px;
  /* El `clip-path` de abajo le da forma de banderín a esta caja, pero el `border` de CSS
     solo se dibuja sobre los 4 lados ORIGINALES del rectángulo -- NO sobre los cortes
     diagonales que crea el clip-path, que es justo donde queda el pedacito que asoma
     (~44% inferior, un triángulo de ~15x29px). El `background` (relleno) sí se pinta en
     toda la caja, así que de ahí depende el contraste real -- ver el degradado en
     --bottom/--top más abajo. Sin `border` -- igual que `.ai-modal` en modo small (ver su
     comentario), para que no quede ninguna línea de borde en ningún lado de la pieza
     tarjeta+colita, solo la sombra compartida del shell definiéndola. */
  z-index: 1;
}

/* Apunta hacia abajo (el modal abrió arriba del botón) -- esta caja "nace" pegada al
   borde INFERIOR del modal (y=0%, tapada detrás de la tarjeta) y su punta expuesta queda
   abajo (y=100%, sobre la página). Antes tenía un solo color sólido (#e9e1ff) de punta a
   punta -- se veía como un triangulito pegado de otro color en vez de parte de la misma
   tarjeta. El degradado blanco→lila hace que la base (la que en teoría "sale" de la
   tarjeta) funda con el blanco de `.ai-modal`, y solo la puntita expuesta (la única parte
   que de verdad necesita contraste contra la página) lleve el tono lila. */
.ai-modal-tail--bottom {
  clip-path: polygon(0% 0%, 100% 0%, 100% 55%, 20% 100%);
  background: linear-gradient(180deg, #fff 0%, #e9e1ff 100%);
}

/* Apunta hacia arriba (el modal abrió abajo del botón) -- misma forma espejada: acá la
   caja nace pegada al borde SUPERIOR del modal (y=100%, abajo, tapada) y la punta
   expuesta queda arriba (y=0%). Mismo degradado, pero invertido para que la punta
   (arriba) sea la lila y la base (abajo, contra la tarjeta) sea blanca. */
.ai-modal-tail--top {
  clip-path: polygon(0% 100%, 100% 100%, 100% 45%, 20% 0%);
  background: linear-gradient(180deg, #e9e1ff 0%, #fff 100%);
}
</style>
